
const state = {
  terms: [],
  matchAllTerms: false,
  searchChildCollections: true,
  searchAllCollections: true,
  searchAttributes: ['title', 'description', 'keywords'],
  attributePriority: {
    prKeywordsExact: 1,
    prTitle: 0.85,
    prKeywords: 0.75,
    prDescription: 0.75,
  },
  selectedCollectionForSearch: null,
};

const getters = {
  searchCollections: (state, getters) => {
    if (state.searchAllCollections) return getters.allCollections;
    if (state.searchChildCollections) return getters.selectedCollectionAndChildren;
    return [state.selectedCollectionForSearch || getters.selectedCollection];
  },
  cardsToSearch: (state, getters, rootState) => {
    const { search } = rootState.appState;
    const searchIsActive = search.isActive;
    if (!getters.searchCollections.length || !searchIsActive) return [];

    const { searchCollections } = getters;
    let cards = [];
    searchCollections.forEach((collection) => {
      const collectionCards = rootState.cards.cards[collection];
      if (collectionCards) cards = cards.concat(collectionCards);
    });
    return cards || [];
  },
  searchResults: (state, getters) => {
    const searchTermsNo = state.terms.length;
    if (!searchTermsNo) return getters.cardsToSearch;

    // Get priority for different attributes
    const { prKeywordsExact, prKeywords, prTitle, prDescription } = state.attributePriority; // eslint-disable-line object-curly-newline

    const results = [];
    getters.cardsToSearch.forEach((card) => {
      let score = 0;
      let noOfSearchTermMatches = 0;

      state.terms.forEach((term) => {
        const startScore = score;
        const regExact = new RegExp(`\\b${term}\\b`, 'i');
        const regApprox = new RegExp(`.*${term}.*`, 'i');

        if (card.keywords) {
          card.keywords.forEach((keyword) => {
            if (regExact.test(keyword)) score += prKeywordsExact / searchTermsNo;
            else if (regApprox.test(keyword)) score += prKeywords / searchTermsNo;
          });
        }
        if (regApprox.test(card.title)) score += prTitle / searchTermsNo;
        if (card.description && regApprox.test(card.description)) score += prDescription / searchTermsNo;

        if (score > startScore) noOfSearchTermMatches += 1;
      });

      // Adapt score for number of terms matched, or set to 0 if we need to match all the terms
      const ratioOfTermsMatched = (noOfSearchTermMatches / searchTermsNo);
      if (state.matchAllTerms && ratioOfTermsMatched === 1) score *= 1;
      else if (state.matchAllTerms && ratioOfTermsMatched < 1) score *= 0;
      else score *= (noOfSearchTermMatches / searchTermsNo);

      // If score higher than zero, push in results array
      if (score) results.push(Object.assign({}, card, { score }));
    });
    // Sort and return
    results.sort((a, b) => b.score - a.score);
    return results;
  },
  selectedCollection: (state, getters, rootState) => rootState.collections.selectedCollection,
  selectedCollectionAndChildren: (state, getters, rootState) => {
    const collections = [state.selectedCollectionForSearch || getters.selectedCollection];

    const gatherChildren = (collection) => {
      collection.children.forEach((child) => {
        collections.push(child.id);
        if (child.children.length) gatherChildren(child);
      });
    };
    gatherChildren(rootState.collections.collectionNodes[state.selectedCollectionForSearch || getters.selectedCollection]);

    return collections;
  },
  allCollections: (state, getters, rootState) => Object.keys(rootState.collections.collectionNodes).map(collectionId => Number(collectionId)).filter(collectionId => !!collectionId), // Filter out root collection with id 0
};

const actions = {
  setSearchParams({ getters, commit, dispatch, rootState }, { terms, matchAllTerms, searchChildCollections, searchAllCollections, attributes }) { // eslint-disable-line object-curly-newline
    if (terms) {
      const searchTerms = [];
      terms.forEach((term) => {
        if (term.length < 3) return;
        if (searchTerms.includes(term)) return;

        searchTerms.push(term);
      });

      commit('SetSearchTerms', searchTerms);
    }

    commit('SetSearchOptions', { matchAllTerms, searchChildCollections, searchAllCollections, attributes }); // eslint-disable-line object-curly-newline

    // We need to check if any of the stuff in selectedItems is still in the search results, if not it should be removed (so that items that are not a search result anymore
    // do not still remain in the selectedItems array and may thus accidentally be deleted or moved or whatever)

    // To do: hmm, should I not use the getter in appState for this, as it should return exactly this?
    const selectedInSearch = rootState.appState.selectedItems.find(item => item.id === 'search');
    if (!selectedInSearch) return;

    const searchResultIds = getters.searchResults.map(result => result.id);
    selectedInSearch.cards.forEach((cardId) => {
      if (!searchResultIds.includes(cardId)) dispatch('appState/toggleSelectedItem', { id: 'search', cardId }, { root: true });
    });
  },
};

const mutations = {
  SetSearchTerms(state, terms) {
    if (typeof terms === 'object') state.terms = terms;
    else state.terms = [terms];
  },
  SetSearchOptions(state, { matchAllTerms, searchChildCollections, searchAllCollections, attributes }) { // eslint-disable-line object-curly-newline
    if (searchAllCollections !== undefined) {
      state.searchAllCollections = searchAllCollections;
      if (searchAllCollections) state.searchChildCollections = true;
    }
    if (searchChildCollections !== undefined) state.searchChildCollections = searchChildCollections;
    if (matchAllTerms !== undefined) state.matchAllTerms = matchAllTerms;
    if (attributes) state.searchAttributes = attributes;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
